import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { createClient } from '../utils/supabase/client';

const API = process.env.REACT_APP_BACKEND_URL;
const AuthContext = createContext(null);

const supabase = createClient();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const api = useCallback(() => {
    const instance = axios.create({ baseURL: API, withCredentials: true });
    if (session?.access_token) {
      instance.defaults.headers.common['Authorization'] = `Bearer ${session.access_token}`;
    }
    return instance;
  }, [session]);

  const login = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw { response: { data: { detail: error.message } } };
    return data.user;
  };

  const signup = async (email, password, name) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { name } },
    });
    if (error) throw new Error(error.message);
    // Return both user and whether a session was immediately granted.
    // session is null when Supabase email confirmation is enabled.
    return { user: data.user, hasSession: !!data.session };
  };

  const logout = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, login, signup, logout, api, supabase }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
